import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, findDuplicates, mergeDuplicates } from '../api';
import { CHORD, PLATFORMS } from '../constants';

const BASE_URL = 'https://influencers.chord-agency.com/backend/api';

function authHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export default function AdminActivityPage() {
  const navigate  = useNavigate();
  const user      = getUser();
  const [tab, setTab]             = useState('activity'); // activity | duplicates
  const [dupPlatform, setDupPlatform] = useState('ig');
  const [duplicates, setDuplicates]   = useState([]);
  const [dupLoading, setDupLoading]   = useState(false);
  const [merging, setMerging]         = useState(null);
  const [mergeMsg, setMergeMsg]       = useState('');
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange]     = useState(7); // days
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    load();
  }, [range]);

  async function load() {
    setLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/activity_summary.php?days=${range}`, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setData(json.data || []);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }

  const totalAdds    = data.reduce((s, r) => s + (r.added   || 0), 0);
  const totalDeletes = data.reduce((s, r) => s + (r.deleted || 0), 0);
  const totalEdits   = data.reduce((s, r) => s + (r.edited  || 0), 0);

  async function loadDuplicates() {
    setDupLoading(true); setMergeMsg('');
    try {
      const res = await findDuplicates(dupPlatform);
      setDuplicates(res.duplicates || []);
    } catch(e) { setMergeMsg('Error: ' + e.message); }
    finally { setDupLoading(false); }
  }

  async function handleMerge(dup) {
    setMerging(dup.handle);
    try {
      const keepId   = parseInt(dup.ids[0]);
      const mergeIds = dup.ids.slice(1).map(Number);
      await mergeDuplicates(dupPlatform, keepId, mergeIds);
      setMergeMsg(`✓ Merged ${dup.count} records for "${dup.handle}"`);
      setDuplicates(prev => prev.filter(d => d.handle !== dup.handle));
    } catch(e) { setMergeMsg('✗ ' + e.message); }
    finally { setMerging(null); }
  }

  return (
    <div>
      {/* Header */}
      <div style={{ background: CHORD.softBlack, padding: '1.25rem 2rem', borderBottom: `1px solid ${CHORD.border}` }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: CHORD.beige, letterSpacing: '-0.3px', marginBottom: 8 }}>Admin Panel</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['activity','Activity Log'],['duplicates','🔄 Merge Duplicates']].map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)} style={{
              background: tab === t ? CHORD.coral : 'rgba(245,235,221,0.06)',
              border: `0.5px solid ${tab === t ? CHORD.coral : CHORD.border}`,
              borderRadius: 6, padding: '5px 14px', fontSize: 12,
              color: tab === t ? '#fff' : CHORD.textMuted,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Duplicates Tab */}
      {tab === 'duplicates' && (
        <div style={{ padding: '1.5rem 2rem' }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={dupPlatform} onChange={e => setDupPlatform(e.target.value)} style={{
              background: '#2B2B1F', border: `0.5px solid ${CHORD.border}`, borderRadius: 6,
              padding: '7px 12px', fontSize: 12, color: CHORD.beige, fontFamily: 'inherit',
            }}>
              {PLATFORMS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            <button onClick={loadDuplicates} disabled={dupLoading} style={{
              background: CHORD.coral, border: 'none', borderRadius: 6, padding: '7px 16px',
              fontSize: 12, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
            }}>{dupLoading ? 'Scanning...' : 'Find Duplicates'}</button>
            {mergeMsg && <span style={{ fontSize: 12, color: mergeMsg.startsWith('✓') ? '#1D9E75' : '#E55D4A' }}>{mergeMsg}</span>}
          </div>

          {duplicates.length === 0 && !dupLoading && (
            <div style={{ color: CHORD.textFaint, fontSize: 13, padding: '2rem 0', textAlign: 'center' }}>
              {dupLoading ? 'Scanning...' : 'Click "Find Duplicates" to scan for duplicate handles'}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {duplicates.map((dup, i) => (
              <div key={i} style={{ background: CHORD.bgCard, border: `0.5px solid ${CHORD.border}`, borderRadius: 12, padding: '1.25rem 1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: CHORD.beige, marginBottom: 4 }}>
                      {dup.names[0]}
                      <span style={{ fontSize: 11, color: CHORD.coral, marginLeft: 8, fontWeight: 400 }}>{dup.count} duplicates</span>
                    </div>
                    <div style={{ fontSize: 11, color: CHORD.textFaint }}>{dup.handle}</div>
                  </div>
                  <button onClick={() => handleMerge(dup)} disabled={merging === dup.handle} style={{
                    background: 'rgba(29,158,117,0.15)', border: '0.5px solid rgba(29,158,117,0.4)',
                    borderRadius: 8, padding: '7px 16px', fontSize: 12, color: '#1D9E75',
                    cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
                    opacity: merging === dup.handle ? 0.6 : 1,
                  }}>{merging === dup.handle ? 'Merging...' : '⚡ Merge All'}</button>
                </div>

                {/* Records to be merged */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {dup.ids.map((id, j) => (
                    <div key={id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', borderRadius: 8,
                      background: j === 0 ? 'rgba(29,158,117,0.08)' : 'rgba(245,235,221,0.02)',
                      border: `0.5px solid ${j === 0 ? 'rgba(29,158,117,0.3)' : CHORD.border}`,
                    }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: j === 0 ? '#1D9E75' : CHORD.textFaint, minWidth: 40 }}>
                        {j === 0 ? 'KEEP' : 'MERGE'}
                      </span>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: 13, color: CHORD.beige, fontWeight: 500 }}>{dup.names[j]}</span>
                        <span style={{ fontSize: 11, color: CHORD.textFaint, marginLeft: 8 }}>ID: {id}</span>
                      </div>
                      {dup.tiers[j] && <span style={{ fontSize: 11, color: CHORD.orange }}>{dup.tiers[j]}</span>}
                      {dup.niches[j] && <span style={{ fontSize: 11, color: CHORD.textFaint }}>{dup.niches[j]}</span>}
                      {dup.followers[j] > 0 && <span style={{ fontSize: 11, color: CHORD.textFaint }}>{parseInt(dup.followers[j]).toLocaleString()} followers</span>}
                    </div>
                  ))}
                </div>

                {/* Merged result preview */}
                <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(44,124,229,0.06)', borderRadius: 8, border: `0.5px solid rgba(44,124,229,0.2)`, fontSize: 11, color: CHORD.textMuted }}>
                  After merge → Tier: <strong style={{color:CHORD.beige}}>{dup.tiers.join(', ')}</strong>
                  {' · '} Niche: <strong style={{color:CHORD.beige}}>{dup.niches.join(', ')}</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Tab */}
      {tab === 'activity' && (
      <div style={{ padding: '1.5rem 2rem' }}>
        {/* Range selector */}
        <div style={{ display: 'flex', gap: 8, marginBottom: '1.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: CHORD.textMuted }}>Show last:</span>
          {[1, 7, 14, 30].map(d => (
            <button key={d} onClick={() => setRange(d)} style={{
              background: range === d ? CHORD.coral : 'rgba(245,235,221,0.06)',
              border: `0.5px solid ${range === d ? CHORD.coral : CHORD.border}`,
              borderRadius: 6, padding: '5px 14px', fontSize: 12,
              color: range === d ? '#fff' : CHORD.textMuted,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>{d === 1 ? 'Today' : `${d} days`}</button>
          ))}
        </div>

        {/* Summary cards */}
        <div style={{ display: 'flex', gap: 12, marginBottom: '1.5rem' }}>
          <div style={{ background: 'rgba(29,158,117,0.1)', border: '0.5px solid rgba(29,158,117,0.3)', borderRadius: 12, padding: '1rem 1.5rem', minWidth: 140 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#1D9E75' }}>{totalAdds}</div>
            <div style={{ fontSize: 11, color: CHORD.textMuted, textTransform: 'uppercase', letterSpacing: '.8px', marginTop: 2 }}>Total Added</div>
          </div>
          <div style={{ background: 'rgba(229,93,74,0.1)', border: '0.5px solid rgba(229,93,74,0.3)', borderRadius: 12, padding: '1rem 1.5rem', minWidth: 140 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: CHORD.coral }}>{totalDeletes}</div>
            <div style={{ fontSize: 11, color: CHORD.textMuted, textTransform: 'uppercase', letterSpacing: '.8px', marginTop: 2 }}>Total Deleted</div>
          </div>
          <div style={{ background: 'rgba(44,124,229,0.1)', border: '0.5px solid rgba(44,124,229,0.3)', borderRadius: 12, padding: '1rem 1.5rem', minWidth: 140 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#2C7CE5' }}>{totalEdits}</div>
            <div style={{ fontSize: 11, color: CHORD.textMuted, textTransform: 'uppercase', letterSpacing: '.8px', marginTop: 2 }}>Total Edited</div>
          </div>
        </div>

        {/* Table */}
        {loading && <div style={{ color: CHORD.textFaint, fontSize: 13 }}>Loading...</div>}
        {error   && <div style={{ color: CHORD.coral, fontSize: 13 }}>Error: {error}</div>}

        {!loading && !error && (
          <div style={{ background: CHORD.bgCard, border: `0.5px solid ${CHORD.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(245,235,221,0.04)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: CHORD.textMuted, textTransform: 'uppercase', letterSpacing: '.8px', borderBottom: `0.5px solid ${CHORD.border}` }}>User</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#1D9E75', textTransform: 'uppercase', letterSpacing: '.8px', borderBottom: `0.5px solid ${CHORD.border}` }}>Added</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: CHORD.coral, textTransform: 'uppercase', letterSpacing: '.8px', borderBottom: `0.5px solid ${CHORD.border}` }}>Deleted</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#2C7CE5', textTransform: 'uppercase', letterSpacing: '.8px', borderBottom: `0.5px solid ${CHORD.border}` }}>Edited</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: CHORD.textMuted, textTransform: 'uppercase', letterSpacing: '.8px', borderBottom: `0.5px solid ${CHORD.border}` }}>Last Active</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: '2rem', textAlign: 'center', color: CHORD.textFaint, fontSize: 13 }}>No activity in this period.</td></tr>
                )}
                {data.map((row, i) => (
                  <tr key={i} style={{ borderBottom: `0.5px solid ${CHORD.border}` }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,235,221,0.02)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: CHORD.beige }}>{row.user_email}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {row.added > 0
                        ? <span style={{ background: 'rgba(29,158,117,0.15)', color: '#1D9E75', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600 }}>+{row.added}</span>
                        : <span style={{ color: CHORD.textFaint }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {row.deleted > 0
                        ? <span style={{ background: 'rgba(229,93,74,0.15)', color: CHORD.coral, borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600 }}>−{row.deleted}</span>
                        : <span style={{ color: CHORD.textFaint }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {row.edited > 0
                        ? <span style={{ background: 'rgba(44,124,229,0.15)', color: '#2C7CE5', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600 }}>{row.edited}</span>
                        : <span style={{ color: CHORD.textFaint }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, color: CHORD.textMuted }}>
                      {row.last_active ? new Date(row.last_active).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
